import React from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { map } from 'lodash-es';
import { useIntl } from 'react-intl';

import { ICombo, IItem, IPicker, IPickerOption, ISection, MenuObject } from '@rbi-ctg/menu';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useNewMainMenuContext } from 'state/main-menu';
import { useMenuOptionsContext } from 'state/menu-options';
import { useUIContext } from 'state/ui';

interface IFormatMenuPriceProps {
  menuItem: ICombo | IItem | MenuObject;
}

const isPickerOption = (
  menuItem: IPicker | ISection,
  option: IPickerOption | MenuObject
): option is IPickerOption => {
  return option && menuItem._type === 'picker';
};

const getPriceForMenuObject = (
  menuItem: MenuObject,
  getPricingAndAvailability: Function,
  formatCurrencyForLocale: Function
) => {
  const pricingAndAvailability = getPricingAndAvailability(menuItem._id);

  if (!pricingAndAvailability) {
    return null;
  }

  const { price } = pricingAndAvailability;

  if (price?.default) {
    return <>{formatCurrencyForLocale(price.default)}</>;
  }

  if (!price?.default && price?.min) {
    return <>{formatCurrencyForLocale(price.min)}</>;
  }

  return null;
};

const FormatMenuPrice: React.FC<IFormatMenuPriceProps> = ({ menuItem }) => {
  const { pricingFunction } = useMenuOptionsContext();
  const { getPricingAndAvailability } = useNewMainMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const { formatMessage } = useIntl();
  const enableMenuServiceData = useFlag(LaunchDarklyFlag.ENABLE_MENU_SERVICE_DATA);

  if (!menuItem) {
    return null;
  }

  // If Menu Service Data is enabled then always just return whatever pricing menu-service has
  if (enableMenuServiceData) {
    return getPriceForMenuObject(menuItem, getPricingAndAvailability, formatCurrencyForLocale);
  }

  // when menuItem is just an item
  // Item -> look up price of item.vendorConfigs[POS] (ItemOptions are ignored)
  if (menuItem._type === 'item') {
    return <>{formatCurrencyForLocale(pricingFunction(menuItem))}</>;
  }

  // when menuItem is just an combo
  // Combo -> look up price of combo.vendorConfigs[POS] (ComboSlots are ignored)
  if (menuItem._type === 'combo') {
    return <>{formatCurrencyForLocale(pricingFunction(menuItem))}</>;
  }

  // Picker & Section -> find minPrice & maxPrice from all options underneath
  // if minPrice == maxPrice display by itself
  // else display minPrice - maxPrice
  if (menuItem._type === 'picker' || menuItem._type === 'section') {
    if (!menuItem.options || menuItem.options.length === 0) {
      return null;
    }

    const optVals = map(menuItem.options, (option: IPickerOption | MenuObject) => {
      // this will not work for a section in a section
      const opt = isPickerOption(menuItem, option) ? option.option : option;
      return opt ? pricingFunction(opt) : 0;
    });

    const minPrice = Math.min(...optVals);
    const maxPrice = Math.max(...optVals);

    if (isNaN(minPrice) || isNaN(maxPrice)) {
      return null;
    }

    if (minPrice === maxPrice) {
      return <>{formatCurrencyForLocale(Math.floor(minPrice))}</>;
    }

    // if the max value isn't the result of adding a range use a / to denote either/or
    const hasTwoOptions = optVals.length === 2;

    return (
      <>
        {formatCurrencyForLocale(Math.floor(minPrice))}
        <span aria-hidden="true">&nbsp;{hasTwoOptions ? '/' : '-'}&nbsp;</span>
        <VisuallyHidden>{formatMessage({ id: hasTwoOptions ? 'or' : 'to' })}</VisuallyHidden>
        {formatCurrencyForLocale(Math.floor(maxPrice))}
      </>
    );
  }

  return null;
};

export default FormatMenuPrice;
